import * as React from "react";
import {} from "@blueprintjs/core";
import { useCommonHooks } from "../../../hooks";
import { ActionCode, AgentEditDto } from "../../../api";
import { useCrudApi } from "../../../hooks/useCrudApi";
import {
  FGCheckboxInput,
  FGDateInput,
  FGTextInput,
  FlexFieldGroup,
  showSuccess,
  useFGFormik,
} from "nsitools-react";

import * as Yup from "yup";
import { CardFieldGroup, CustomFormGenerator } from "../../../components";
import GradeSelect from "../../../components/selects/GradeSelect";
import CadreSelect from "../../../components/selects/CadreSelect";
import GradeStatusSelect from "../../../components/selects/GradeStatusSelect";
import AgentSelect from "../../../components/selects/AgentSelect";
import GenderSelect from "../../../components/selects/GenderSelect";
import DirectionSelect from "../../../components/selects/DirectionSelect";
import MentionSelect from "../../../components/selects/MentionSelect";
import GradeSubStatusSelect from "../../../components/selects/GradeSubStatusSelect";
import { ERoutes } from "../../../AppRouter";

export interface AgentDetailPanelProps {
  agentId: number;
  agentIsLinkedToDossier: boolean;
}

export const AgentDetailPanel: React.FunctionComponent<
  AgentDetailPanelProps
> = ({ agentId, agentIsLinkedToDossier }) => {
  const { apis, t, hasAction, router, isSuperAdmin, user } = useCommonHooks();
  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditAgent]) && agentId !== user.agentId,
    [agentId, hasAction, user.agentId]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: ["agent-details", agentId],
      fetchFn: async () => {
        if (agentId > 0) {
          return apis.agents.get(agentId);
        }
        const newUser = AgentEditDto.fromJS({});
        newUser.isActive = true;

        return newUser;
      },
      saveFn: async (toSave: AgentEditDto) => {
        return apis.agents.save(toSave);
      },
      onSaved: (itemSaved) => {
        showSuccess(t("AgentSaved"));
        refetch();

        if (agentId <= 0) {
          const routeToPush = router.getEditRoute(
            ERoutes.agents,
            itemSaved.id,
            "details"
          );
          router.push(routeToPush);
        }
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        firstname: Yup.string().required(t("Required")).max(50),
        name: Yup.string().required(t("Required")).max(50),
        email: Yup.string().required(t("Required")),
        sex: Yup.string().nullable().required(t("Required")),
        nationalRegistrationNumber: Yup.string()
          .nullable()
          .required(t("Required"))
          .max(13),
        gradeStartDate: Yup.date().nullable().required(t("Required")),
        cadreId: Yup.number().nullable().required(t("Required")),
        gradeStatusId: Yup.number().nullable().required(t("Required")),
      }),
    [t]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  const areFieldsReadonly = React.useMemo(() => {
    if (isSuperAdmin) {
      return false;
    }
    return agentIsLinkedToDossier;
  }, [agentIsLinkedToDossier, isSuperAdmin]);

  const minDate = React.useMemo(() => {
    const date = new Date();
    date.setFullYear(1980, 0, 1);

    return date;
  }, []);

  return (
    <FlexFieldGroup>
      <CardFieldGroup title={t("Details")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <FlexFieldGroup columns={2}>
            <FlexFieldGroup>
              <FGTextInput
                name="name"
                label={t("LastName")}
                readonly={agentIsLinkedToDossier}
              />
              <FGTextInput
                name="firstname"
                label={t("FirstName")}
                readonly={agentIsLinkedToDossier}
              />
              <GenderSelect
                name="sex"
                label={t("Gender")}
                readonly={agentIsLinkedToDossier}
              />
              <FGTextInput
                name="nationalRegistrationNumber"
                label={t("NationalRegisterNumber")}
                readonly={agentIsLinkedToDossier}
              />
              <FGTextInput
                name="email"
                label={t("Email")}
                readonly={agentIsLinkedToDossier}
              />
              <GradeSelect
                name="gradeId"
                label={t("Grade")}
                readonly={agentIsLinkedToDossier}
              />
              <FGDateInput
                name="gradeStartDate"
                label={t("GradeStartDate")}
                readonly={agentIsLinkedToDossier}
                minDate={minDate}
              />
              <CadreSelect
                name="cadreId"
                label={t("Cadre")}
                readonly={agentIsLinkedToDossier}
              />
              <GradeStatusSelect
                name="gradeStatusId"
                label={t("GradeStatus")}
                readonly={agentIsLinkedToDossier}
              />
              <GradeSubStatusSelect
                name={"gradeSubStatusId"}
                label={t("GradeSubStatus")}
                readonly={agentIsLinkedToDossier}
              />
              <FGTextInput
                name="affectation"
                label={t("Affectation")}
                readonly={agentIsLinkedToDossier}
              />
              <DirectionSelect
                name="directionId"
                label={t("DirectionOrService")}
                readonly={agentIsLinkedToDossier}
              />
              <FGCheckboxInput
                name="isActive"
                label={t("IsActive")}
                switchMode
                readonly={false}
              />
              <FGCheckboxInput
                name="hasSkillCertificate"
                label={t("HasSkillCertificate")}
                switchMode
                labelStyles={{
                  display: "flex",
                  maxWidth: 180,
                  wordBreak: "break-word",
                }}
                readonly={false}
              />
            </FlexFieldGroup>
            <FlexFieldGroup>
              <AgentSelect
                name={"agentN1Id"}
                label={t("NPlus1")}
                exceptAgentId={formik?.values?.id}
                readonly={areFieldsReadonly}
              />
              <AgentSelect
                name={"agentN2Id"}
                label={t("NPlus2")}
                exceptAgentId={formik?.values?.id}
                readonly={areFieldsReadonly}
              />
              <MentionSelect
                name="mentionId"
                label={t("Mention")}
                onlyActive={true}
                readonly={agentIsLinkedToDossier}
              />
            </FlexFieldGroup>
          </FlexFieldGroup>
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default AgentDetailPanel;
